import {FlexRow, Modal, TextField} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {MAX_SECTION_TITLE_CHARS} from '../config/defaultTemplate';
import {ModalProps} from '../../../../forecast-app/shared/components/modals/types';

export type EditSectionTitleModalPayload = {
	/** The stored override, or empty when the section still uses its default label. */
	currentTitle: string;
	/** The localized default, shown as the placeholder so clearing the field reads as "go back to this". */
	defaultTitle: string;
	onSave: (title: string) => void;
};

export const showEditSectionTitleModal = (props: EditSectionTitleModalPayload): void => {
	showModal({
		type: MODAL_TYPE.EDIT_SECTION_TITLE,
		...props,
	});
};

/**
 * Renames one section's heading for this quote only. The section's type, content and toggle state
 * are untouched — this is a label override, not a template change.
 */
export const EditSectionTitleModal = ({
	closeModal,
	currentTitle,
	defaultTitle,
	onSave,
}: ModalProps<EditSectionTitleModalPayload>): JSX.Element => {
	const intl = useIntl();
	const [draft, setDraft] = useState(currentTitle);

	const handleSave = () => {
		onSave(draft);
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{intl.formatMessage({id: 'quote_designer.section_title.modal_title'})}</Modal.Title>
			<Modal.Content>
				<TextField
					label={intl.formatMessage({id: 'quote_designer.section_title.field'})}
					placeholder={defaultTitle}
					value={draft}
					onValueChange={setDraft}
					// A heading is a label, and one long enough to decide the layout of its own page is
					// not one. The renderers clip anything longer; stopping it here means what the
					// author typed is what they see.
					maxLength={MAX_SECTION_TITLE_CHARS}
					data-cy="quote-section-title-field"
				/>
				<FlexRow>
					<Modal.PrimaryFooterButton onPress={handleSave} dataCy="quote-section-title-save">
						{intl.formatMessage({id: 'common.save'})}
					</Modal.PrimaryFooterButton>
					<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
						{intl.formatMessage({id: 'common.cancel'})}
					</Modal.SecondaryFooterButton>
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
};
